import { useReadQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import { Box, SxProps, Theme } from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
  GridRowParams,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRowId,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  useGridApiRef,
  GridApiPremium,
  GridSlotsComponent,
} from "@mui/x-data-grid-premium";
import { DataGrid, DataGridProps } from "components/DataGrid";
import { useCurrentUser } from "hooks/useCurrentUser";
import { Suspense, useMemo, useEffect, ReactNode, memo } from "react";

import { DataGridSkeleton } from "./DataGridSkeleton";
import { useDevWarnUnstableProps } from "./useDevWarnUnstableProps";

// Transition duration for opacity changes
const TRANSITION_DURATION = "150ms";

export interface SuspenseDataGridProps<TData, TVariables, TRow extends GridValidRowModel> {
  /**
   * Query ref from useDataGridQuery
   */
  queryRef: QueryRef<TData, TVariables> | null;
  /**
   * Whether a refetch is pending (shows opacity transition)
   */
  isPending?: boolean;
  /**
   * Extract items array from query data
   */
  getItems: (data: TData) => TRow[];
  /**
   * Extract total count from query data
   */
  getCount: (data: TData) => number;
  /**
   * Callback when items are loaded. Can be used to cache row data for selection.
   */
  onItemsLoaded?: (items: TRow[]) => void;
  /**
   * Grid columns
   */
  columns: GridColDef<TRow>[];
  /**
   * Pagination model
   */
  paginationModel: GridPaginationModel;
  /**
   * Pagination change handler
   */
  onPaginationModelChange: (model: GridPaginationModel) => void;
  /**
   * Sort model
   */
  sortModel?: GridSortModel;
  /**
   * Sort change handler
   */
  onSortModelChange?: (model: GridSortModel) => void;
  /**
   * Column visibility model
   */
  columnVisibilityModel?: GridColumnVisibilityModel;
  /**
   * Column visibility change handler
   */
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  /**
   * Row selection model
   */
  rowSelectionModel?: GridRowSelectionModel;
  /**
   * Row selection change handler
   */
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  /**
   * Row click handler
   */
  onRowClick?: (params: GridRowParams<TRow>) => void;
  /**
   * Check if row is selectable
   */
  isRowSelectable?: (params: GridRowParams<TRow>) => boolean;
  /**
   * Get unique row ID
   */
  getRowId?: (row: TRow) => GridRowId;
  /**
   * Row height
   */
  rowHeight?: number | "auto";
  /**
   * Get row height dynamically
   */
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
  /**
   * Enable checkbox selection
   */
  checkboxSelection?: boolean;
  /**
   * Disable select all checkbox
   */
  disableSelectAll?: boolean;
  /**
   * Disable row selection on click
   */
  disableRowSelectionOnClick?: boolean;
  /**
   * Hide footer
   */
  hideFooter?: boolean;
  /**
   * Page size options
   */
  pageSizeOptions?: number[];
  /**
   * Grid sx props
   */
  sx?: SxProps<Theme>;
  /**
   * Initial grid state
   */
  initialState?: GridInitialState;
  /**
   * Detail panel content
   */
  getDetailPanelContent?: (params: GridRowParams<TRow>) => ReactNode;
  /**
   * Detail panel height
   */
  getDetailPanelHeight?: (params: GridRowParams<TRow>) => number | "auto";
  /**
   * Get row link (for navigation)
   */
  getRowLink?: DataGridProps<TRow>["getRowLink"];
  /**
   * CSS class name(s) applied per row (e.g. unread-row styling).
   */
  getRowClassName?: DataGridProps<TRow>["getRowClassName"];
  /**
   * API ref
   */
  apiRef?: React.RefObject<GridApiPremium | null>;
  /**
   * Message to display when no rows
   */
  noRowsMessage?: string;
  /**
   * Custom slots for the DataGrid (e.g., noRowsOverlay).
   * Use module-level constants for stable references.
   */
  slots?: Partial<GridSlotsComponent>;
  /**
   * Fallback content while loading (initial render)
   */
  fallback?: ReactNode;
}

/**
 * Props type for SuspenseDataGridInner
 */
type SuspenseDataGridInnerProps<TData, TVariables, TRow extends GridValidRowModel> = Omit<
  SuspenseDataGridProps<TData, TVariables, TRow>,
  "fallback" | "isPending"
> & {
  queryRef: QueryRef<TData, TVariables>;
};

/**
 * Inner component that reads from the query ref.
 * This suspends until data is available.
 *
 * Wrapped with React.memo to break parent render cascades.
 * The grid should only re-render when its own props meaningfully change,
 * not when parent components re-render.
 */
const SuspenseDataGridInnerBase = memo(function SuspenseDataGridInnerBase<
  TData,
  TVariables,
  TRow extends GridValidRowModel,
>({
  queryRef,
  getItems,
  getCount,
  onItemsLoaded,
  columns,
  paginationModel,
  onPaginationModelChange,
  sortModel = [],
  onSortModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  rowSelectionModel,
  onRowSelectionModelChange,
  onRowClick,
  isRowSelectable,
  getRowId,
  rowHeight = 38,
  getRowHeight,
  checkboxSelection = false,
  disableSelectAll = false,
  disableRowSelectionOnClick,
  hideFooter,
  pageSizeOptions,
  sx,
  initialState,
  getDetailPanelContent,
  getDetailPanelHeight,
  getRowLink,
  getRowClassName,
  apiRef: externalApiRef,
  noRowsMessage,
  slots,
}: SuspenseDataGridInnerProps<TData, TVariables, TRow>) {
  // Dev-only: warn when a memo-eligible prop changes identity between renders.
  // If these aren't memoized in the parent, the React.memo wrapper bails out
  // on every parent render and the grid re-renders unnecessarily.
  useDevWarnUnstableProps("SuspenseDataGridInner", {
    columns,
    getItems,
    getCount,
    getRowLink,
    onRowClick,
    getRowId,
    getRowHeight,
    isRowSelectable,
    getDetailPanelContent,
    getDetailPanelHeight,
    slots,
  });

  const { user } = useCurrentUser();
  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? internalApiRef;

  // Read data from query ref - this suspends until data is ready
  const { data } = useReadQuery(queryRef);

  const items = useMemo(() => getItems(data), [data, getItems]);
  const count = useMemo(() => getCount(data), [data, getCount]);

  // Notify when items are loaded (for selection cache updates)
  // Using useEffect to avoid calling during render
  useEffect(() => {
    if (onItemsLoaded && items.length > 0) {
      onItemsLoaded(items);
    }
  }, [items, onItemsLoaded]);

  // Memoize row height function
  const memoizedGetRowHeight = useMemo(
    () => getRowHeight ?? (() => rowHeight),
    [getRowHeight, rowHeight]
  );

  // Memoize isRowSelectable
  const memoizedIsRowSelectable = useMemo(
    () =>
      isRowSelectable
        ? (params: GridRowParams<TRow>) => !!params.row && isRowSelectable(params)
        : undefined,
    [isRowSelectable]
  );

  // IMPORTANT: DataGrid sx must be stable to prevent full row re-renders.
  // Moving opacity/pointerEvents to wrapper keeps DataGrid props stable during pending state.
  const gridSx = useMemo(
    (): SxProps<Theme> => ({
      // V2-specific styling: ensure minimum height for consistent layout
      "& .MuiDataGrid-virtualScroller": {
        minHeight: 300,
      },
      // Style noRows/noResults overlays for V2
      "& .MuiDataGrid-overlayWrapper": {
        minHeight: 300,
      },
      "& .MuiDataGrid-overlayWrapperInner": {
        minHeight: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      // Disable select all when needed
      ...(disableSelectAll && {
        "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
          display: "none",
        },
      }),
      ...(typeof sx === "object" ? sx : {}),
    }),
    [disableSelectAll, sx]
  );

  if (!user) return null;

  return (
    <DataGrid
      apiRef={apiRef}
      columns={columns}
      rows={items}
      getRowHeight={memoizedGetRowHeight}
      autoHeight={rowHeight === "auto"}
      getRowId={getRowId}
      sx={gridSx}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      // Never pass loading=true - we handle loading via opacity
      loading={false}
      rowCount={count}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={onRowSelectionModelChange}
      isRowSelectable={memoizedIsRowSelectable}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      keepNonExistentRowsSelected
      onRowClick={onRowClick}
      columnVisibilityModel={columnVisibilityModel}
      initialState={initialState}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      hideFooterPagination={false}
      hideFooter={hideFooter}
      getRowLink={getRowLink}
      getRowClassName={getRowClassName}
      pageSizeOptions={pageSizeOptions}
      noRowsMessage={noRowsMessage}
      slots={slots}
    />
  );
});

// Set display name for React DevTools and Bolt tracking
SuspenseDataGridInnerBase.displayName = "SuspenseDataGridInner";

// Type assertion to preserve generic types through memo
const SuspenseDataGridInner = SuspenseDataGridInnerBase as <
  TData,
  TVariables,
  TRow extends GridValidRowModel,
>(
  props: SuspenseDataGridInnerProps<TData, TVariables, TRow>
) => React.ReactElement | null;

/**
 * Suspense-enabled DataGrid component.
 *
 * Uses Apollo's useReadQuery which suspends until data is available.
 * Loading states are shown via opacity transition instead of a spinner overlay.
 *
 * IMPORTANT: The opacity wrapper is intentionally OUTSIDE the memoized inner component.
 * This prevents row re-renders when isPending changes - only the opacity CSS updates.
 */
export function SuspenseDataGrid<TData, TVariables, TRow extends GridValidRowModel>(
  props: SuspenseDataGridProps<TData, TVariables, TRow>
) {
  const { queryRef, fallback, isPending = false, ...rest } = props;

  // If no query ref (e.g., skip=true), show nothing or fallback
  if (!queryRef) {
    return fallback ? <>{fallback}</> : null;
  }

  return (
    <Box
      sx={{
        opacity: isPending ? 0.6 : 1,
        transition: `opacity ${TRANSITION_DURATION} ease-in-out`,
        pointerEvents: isPending ? "none" : "auto",
      }}
    >
      <Suspense fallback={fallback ?? <DataGridSkeleton />}>
        <SuspenseDataGridInner queryRef={queryRef} {...rest} />
      </Suspense>
    </Box>
  );
}
